"use client";

import { useEffect, useState } from 'react';
import { usePathname, useRouter } from 'next/navigation';
import { hasValidToken } from '@/utils/token';

interface AuthGuardProps {
  children: React.ReactNode;
  excludeRoutes?: string[]; // List of routes to exclude from auth check
}

export function AuthGuard({ children, excludeRoutes = [] }: AuthGuardProps) {
  const router = useRouter();
  const pathname = usePathname();
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  useEffect(() => {
    const verifyUser = async () => {
      // Check if mobile and not in standalone mode first
      const isMobile = window.innerWidth <= 768;
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      
      // Exclude install page and reset password from mobile install guard
      const installGuardExcludedRoutes = ['/install', '/auth/reset-password'];
      
      if (isMobile && !isStandalone && !installGuardExcludedRoutes.includes(pathname)) {
        router.push('/install');
        return;
      }

      // Then check if the current route is excluded
      if (excludeRoutes.includes(pathname)) {
        setLoading(false);
        setIsAuthenticated(true);
        return;
      }

      const authenticated = hasValidToken();
      if (!authenticated) {
        router.push('/auth');
      } else {
        setIsAuthenticated(true);
      }
      setLoading(false);
    };

    verifyUser();
  }, [router, pathname, excludeRoutes]);

  if (loading) {
    return;
  }

  return <>{isAuthenticated && children}</>;
}